import React, { useMemo } from "react";
import { PartnerData } from "@/pages/partners/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { Filters } from "@/services/api/types";
import { useEntityFields } from "@/pages/settings/hooks";
import { getPartnerBaseFilters } from "@/pages/partners/helpers";
import InvolvedIn from "@/components/layouts/InvolvedIn";
import NextSteps from "@/components/layouts/NextSteps/NextSteps";
import PartnerPeopleSection from "@/components/layouts/QuickView/PartnerPeopleSection/PartnerPeopleSection";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";

interface Props {
  partner: PartnerData | undefined;
  isQuickView?: boolean;
  getCreateOptions?: () => DetailedViewOptions;
}

const orUndefined = (filter: Filters): Filters | undefined =>
  Object.keys(filter).length > 0 ? filter : undefined;

const PartnerSections: React.FC<Props> = ({
  partner,
  isQuickView = false,
  getCreateOptions
}) => {
  const partnerId = partner?.id;
  const createOptions = getCreateOptions?.();

  const getActionFunction = (option: unknown): (() => void) | undefined =>
    typeof option === "function"
      ? (option as () => void)
      : (option as { action?: () => void })?.action;

  const { data: leadFields } = useEntityFields(EntityType.LEAD);
  const { data: dealFields } = useEntityFields(EntityType.DEAL);

  const partnerFilters = useMemo(
    () => ({
      leads: orUndefined(getPartnerBaseFilters(leadFields?.fields, partnerId)),
      deals: orUndefined(getPartnerBaseFilters(dealFields?.fields, partnerId))
    }),
    [leadFields, dealFields, partnerId]
  );

  return (
    <>
      <InvolvedIn
        entityId={partnerId || ""}
        entityType={EntityType.PARTNER}
        className="min-h-fit"
        isQuickView={isQuickView}
        partnerFilters={partnerFilters}
        onCreateLead={getActionFunction(createOptions?.lead)}
        onCreateDeal={getActionFunction(createOptions?.deal)}
      />
      <NextSteps
        className="w-full px-4"
        entity={partner}
        entityType={EntityType.PARTNER}
        isQuickView={isQuickView}
      />
      {/* The detailed view has a dedicated People tab, so only the quick view
          (no tabs) shows the partner people section. */}
      {isQuickView ? <PartnerPeopleSection partnerId={partnerId} /> : null}
    </>
  );
};

export default PartnerSections;
